import { useState, useMemo } from 'react';
import {
  ArrowLeft,
  Heart,
  MessageCircle,
  TrendingUp,
  Clock,
  ThumbsUp,
} from 'lucide-react';
import { useSocialFeed } from '../../context/SocialFeedContext';
import type { SocialPost } from './socialTypes';
import { SocialPostImage } from './SocialPostImage';
import { PostDetailModal } from './PostDetailModal';

interface TrendingPetFeedViewProps {
  pet: {
    id: string;
    name: string;
    avatar: string;
    image: string;
    ownerName?: string;
    ownerAvatar?: string;
  };
  posts: SocialPost[];
  userId: string;
  onBack: () => void;
  followedPetIds: Set<string>;
  followLoadingPetId: string | null;
  onFollowPet: (petId: string, petName: string) => void;
  onViewPetProfile?: (petId: string) => void;
}

type SortMode = 'recent' | 'liked' | 'trending';

/** Pure scoring fn mirroring ExplorePage.scorePost */
function scoreSocialPost(post: SocialPost, now: number): number {
  const comments = post.comments ?? [];
  const likes = post.likes ?? 0;
  const createdAtMs =
    typeof post.createdAt === 'number' ? post.createdAt : null;
  const ageHours =
    createdAtMs != null ? Math.max(0, (now - createdAtMs) / 3_600_000) : 72;
  const quality = likes * 3 + comments.length * 5;
  const velocity = ageHours < 24 ? 2.0 : ageHours < 48 ? 1.5 : 1.0;
  const recency = Math.max(0, 48 - ageHours);
  const unique = Math.min(comments.length * 2, 20);
  const credibility = post.verified ? 15 : 0;
  const discussion = comments.length > 3 ? 10 : 0;
  const media = (post.images?.length ?? 0) > 0 ? 10 : 0;
  const score =
    quality * velocity + recency + unique + credibility + discussion + media;
  return isNaN(score) ? 0 : score;
}

export function TrendingPetFeedView({
  pet,
  posts,
  userId: _userId,
  onBack,
  followedPetIds,
  followLoadingPetId,
  onFollowPet,
  onViewPetProfile,
}: TrendingPetFeedViewProps) {
  const { likePost, unlikePost, addComment } = useSocialFeed();
  const [sort, setSort] = useState<SortMode>('recent');
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);

  const petPosts = useMemo(
    () => posts.filter((p) => (p.taggedPetIds ?? []).includes(pet.id)),
    [posts, pet.id]
  );

  const sortedPosts = useMemo(() => {
    const now = Date.now();
    const list = [...petPosts];
    if (sort === 'recent') {
      list.sort((a, b) => {
        const aT = typeof a.createdAt === 'number' ? a.createdAt : 0;
        const bT = typeof b.createdAt === 'number' ? b.createdAt : 0;
        return bT - aT;
      });
    } else if (sort === 'liked') {
      list.sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0));
    } else {
      list.sort((a, b) => scoreSocialPost(b, now) - scoreSocialPost(a, now));
    }
    return list;
  }, [petPosts, sort]);

  const sortOptions: {
    id: SortMode;
    label: string;
    icon: React.ElementType;
  }[] = [
    { id: 'recent', label: 'Recent', icon: Clock },
    { id: 'liked', label: 'Most Liked', icon: ThumbsUp },
    { id: 'trending', label: 'Trending', icon: TrendingUp },
  ];

  const isFollowed = followedPetIds.has(pet.id);

  return (
    <div className="min-w-0">
      {/* Back bar */}
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-[#936FEF] hover:text-[#7B5CE5] transition-colors mb-4 font-medium"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Trending
      </button>

      {/* Pet header card */}
      <div className="relative rounded-2xl overflow-hidden mb-5 shadow-md border border-[#E2DFEA] dark:border-white/8">
        {/* Background */}
        <div className="absolute inset-0">
          {pet.image ? (
            <SocialPostImage
              src={pet.image}
              alt={pet.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#936FEF] to-[#2D2F7E]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        </div>

        {/* Content */}
        <div className="relative px-4 pt-8 pb-4 flex items-end gap-3">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white/30 shadow-lg shrink-0 bg-white/20">
            {pet.avatar?.startsWith('http') ? (
              <img
                src={pet.avatar}
                alt={pet.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-3xl">
                {pet.avatar || '🐾'}
              </div>
            )}
          </div>

          {/* Name + meta */}
          <div className="flex-1 min-w-0">
            <h2
              className="text-xl font-bold text-white truncate"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {pet.name}
            </h2>
            {pet.ownerName && (
              <p
                className="text-xs text-white/70 truncate"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                by {pet.ownerName}
              </p>
            )}
            <p
              className="text-xs text-white/60 mt-0.5"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {petPosts.length} {petPosts.length === 1 ? 'post' : 'posts'}{' '}
              tagged
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 shrink-0">
            <button
              type="button"
              onClick={() => onFollowPet(pet.id, pet.name)}
              disabled={followLoadingPetId === pet.id}
              className="px-4 py-2 rounded-xl text-sm font-bold transition-all hover:opacity-90 active:scale-95 shadow"
              style={{
                fontFamily: 'Sora, sans-serif',
                background: isFollowed
                  ? 'rgba(255,255,255,0.2)'
                  : 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
                color: '#fff',
                backdropFilter: isFollowed ? 'blur(8px)' : undefined,
              }}
            >
              {followLoadingPetId === pet.id
                ? '...'
                : isFollowed
                  ? 'Following'
                  : 'Follow'}
            </button>
            {onViewPetProfile && (
              <button
                type="button"
                onClick={() => onViewPetProfile(pet.id)}
                className="px-4 py-1.5 rounded-xl text-xs font-medium text-white/80 border border-white/30 hover:bg-white/10 transition-colors"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Full profile →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sort toggle */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {sortOptions.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setSort(id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all border ${
              sort === id
                ? 'bg-[#936FEF] text-white border-[#936FEF] shadow-sm'
                : 'bg-white dark:bg-[#16151D] text-[#464452] dark:text-white/60 border-[#E2DFEA] dark:border-white/10 hover:border-[#936FEF]/40'
            }`}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <Icon className="w-3 h-3" />
            {label}
          </button>
        ))}
      </div>

      {/* Post grid */}
      {sortedPosts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="text-5xl mb-4">🐾</span>
          <p
            className="text-base font-semibold text-[#2D2F7E] dark:text-white mb-2"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            No posts tagged with {pet.name} yet
          </p>
          <p
            className="text-sm text-[#464452] dark:text-white/50 max-w-xs"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Be the first! Tag {pet.name} in your next post to help others
            discover them.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
          {sortedPosts.map((post) => (
            <button
              key={post.id}
              type="button"
              onClick={() => setSelectedPost(post)}
              className="group relative aspect-square rounded-xl overflow-hidden bg-[#F0EFF5] dark:bg-white/5 border border-[#E2DFEA] dark:border-white/8 hover:shadow-lg transition-all active:scale-[0.98]"
            >
              {/* Post image / fallback */}
              {(post.images?.length ?? 0) > 0 ? (
                <SocialPostImage
                  src={post.images[0]}
                  alt={post.caption ?? ''}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-start p-3 bg-gradient-to-br from-[#F0EFF5] to-[#E2DFEA] dark:from-white/5 dark:to-white/10">
                  <p
                    className="text-xs text-[#464452] dark:text-white/70 line-clamp-5 text-left"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {post.caption}
                  </p>
                </div>
              )}

              {/* Hover overlay with engagement stats */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity flex items-center justify-center gap-4 rounded-xl">
                <div className="flex items-center gap-1 text-white">
                  <Heart className="w-4 h-4 fill-white" />
                  <span
                    className="text-sm font-semibold"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {post.likes ?? 0}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-white">
                  <MessageCircle className="w-4 h-4 fill-white" />
                  <span
                    className="text-sm font-semibold"
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {(post.comments ?? []).length}
                  </span>
                </div>
              </div>

              {/* Liked indicator badge */}
              {post.isLiked && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center shadow">
                  <Heart className="w-3 h-3 fill-white text-white" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Post detail modal */}
      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onLike={(postId) => {
            const p = petPosts.find((x) => x.id === postId);
            if (!p) return;
            if (p.isLiked) unlikePost(postId);
            else likePost(postId);
          }}
          onAddComment={(postId, text) => addComment(postId, text)}
        />
      )}
    </div>
  );
}
